import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialogModule } from '@angular/material/dialog';
import { NgxMaskModule } from 'ngx-mask';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { InterceptorModule } from '../../src/app/shared/services/ServiceInterceptor';
import { CanActivate } from './shared/services/auth.guard';
import { CanDeactivateGuard } from './shared/services/can-deactivate.guard';
import { HttpCallService } from './shared/services/base.service';
import { SiteLayoutService } from './sitelayout/sitelayout.service';
import { LoaderService } from './shared/services/loader.service'
import { AppConstant } from './app.constants'; 
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { SitelayoutComponent } from './sitelayout/sitelayout.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { CommonLookupData } from './shared/commonHelper/common.service';
import { CommonServiceHelper } from './shared/commonHelper/commonHelper.service';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { CustomDialogComponent } from './shared/custom-dialog/custom-dialog.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { DialogProgramListComponent } from './dialog-program-list/dialog-program-list.component';
import { AdminDashboardComponent } from './dashboard/admin-dashboard/admin-dashboard.component';
import { UserDashboardComponent } from './dashboard/user-dashboard/user-dashboard.component';
import { DateValidator } from './shared/commonHelper/DateValidator';
import { NumberValidator } from './shared/commonHelper/NumberValidator';
import { DateSuffix } from './shared/commonHelper/DateSuffix';
import { LogoutComponent } from './logout/logout.component';
import { InsightmenuNavigationComponent } from './insightmenu-navigation/insightmenu-navigation.component';
import { MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
//import { TimeformtPipe } from './shared/pipes/timeformt.pipe';

const isIE =
  window.navigator.userAgent.indexOf("MSIE ") > -1 ||
  window.navigator.userAgent.indexOf("Trident/") > -1;

const appRoutes: Routes = [
  { path: '', component: LoginComponent, data: { pageTitle: 'User Landing' }, pathMatch: 'full', canActivate: [CanActivate] },
  { path: 'logout', component: LogoutComponent, canActivate: [CanActivate] },
  {
    path: '',
    component: SitelayoutComponent,
    canActivate: [CanActivate],
    children: [
      { path: 'dashboard', component: DashboardComponent, canActivate: [CanActivate] },
      {
        path: 'programs',
        loadChildren: () => import('src/app/programs/programs.module').then(m => m.ProgramsModule),
        data: { pageTitle: 'Program Management' }
      },
      {
        path: 'location',
        loadChildren: () => import('src/app/location/location.module').then(m => m.LocationModule),
        data: { pageTitle: 'Location Management' }
      },
      {
        path: 'users',
        loadChildren: () => import('src/app/users/users.module').then(m => m.UsersModule),
        data: { pageTitle: 'User Management' }
      },
      {
        path: 'samplingplan',
        loadChildren: () => import('src/app/samplingplan/samplingplan.module').then(m => m.SamplingPlanModule),
        data: { pageTitle: 'Sampling Plan Management' }
      },
      {
        path: 'cases',
        loadChildren: () => import('src/app/case-management/case-management.module').then(m => m.CaseManagementModule),
        data: { pageTitle: 'Case Management' }
      },
      {
        path: 'reports',
        loadChildren: () => import('src/app/reports/reports.module').then(m => m.ReportsModule),
        data: { pageTitle: 'Report Management' },
        canActivate: [CanActivate]
      },
      {
        path: 'mims',
        loadChildren: () => import('./marker-inventory/marker-inventory.module').then(m => m.MarkerInventoryModule),
        data: { pageTitle: 'Marker Inventory' }
      },
      {
        path: 'marker-inventory',
        loadChildren: () => import('./marker-inventory/marker-inventory.module').then(m => m.MarkerInventoryModule),
        data: { pageTitle: 'Marker Inventory new' }
      },
      {
        path: 'remote-command',
        loadChildren: () => import('src/app/remote-command/remote-command.module').then(m => m.RemoteCommandModule),
        data: { pageTitle: 'Remote Command Management' }
      },
      { path: 'insight-menu', component: InsightmenuNavigationComponent, canActivate: [CanActivate] },
    ]
  },
  { path: '**', component: PageNotFoundComponent }
];

@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        SitelayoutComponent,
        DashboardComponent,
        ConfirmDialogComponent,
        CustomDialogComponent,
        PageNotFoundComponent,
        DialogProgramListComponent,
        AdminDashboardComponent,
        UserDashboardComponent,
        DateSuffix,
        DateValidator,
        NumberValidator,
        LogoutComponent,
        InsightmenuNavigationComponent,
    ],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            timeOut: 5000,
            positionClass: 'toast-bottom-right',
            closeButton: true,
            tapToDismiss: true
        }),
        NgbModule,
        RouterModule.forRoot(appRoutes, { scrollPositionRestoration: 'enabled' }),
        FormsModule, ReactiveFormsModule, InterceptorModule, NgxSpinnerModule,
        MatTabsModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatRadioModule,
        NgxMaskModule.forRoot(),
        MatDialogModule,
        MsalModule.forRoot(new PublicClientApplication({
            auth: {
                clientId: environment.clientId, // Application (client) ID from the app registration
                authority: "https://login.microsoftonline.com/" + environment.tenant, // The Azure cloud instance and the app's sign-in audience (tenant ID, common, organizations, or consumers)
                redirectUri: environment.redirectUri, // This is your redirect URI,
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
                storeAuthStateInCookie: isIE, // Set to true for Internet Explorer 11
            },
        }), {
            interactionType: InteractionType.Redirect,
            authRequest: {
                scopes: ['user.read']
            }
        }, {
            interactionType: InteractionType.Redirect,
            protectedResourceMap: new Map([
                ['http://graph.microsoft.com/v1.0/me', ['user.read']]
            ])
        })], providers: [HttpCallService, AppConstant, CanDeactivateGuard,
        LoaderService, SiteLayoutService,
        ToastrService,
        CommonLookupData, CommonServiceHelper,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        }, MsalGuard,
        MsalService, provideHttpClient(withInterceptorsFromDi())] })
export class AppModule { }
